import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { notificationService } from '../api/services/notification.service'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { ListPagination } from '../components/ui/list-pagination'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationContext'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { cn } from '../lib/utils'
import type { AppNotification } from '../types/notification'

function getLinkedCaseId(notification: AppNotification): string | null {
  if (notification.case_id) {
    return notification.case_id
  }

  const caseId = notification.data?.case_id
  if (typeof caseId === 'string' && caseId.length > 0) {
    return caseId
  }

  if (typeof caseId === 'number') {
    return String(caseId)
  }

  return null
}

function getLinkedSupportRequestId(notification: AppNotification): string | null {
  const supportRequestId = notification.data?.support_request_id
  if (typeof supportRequestId === 'string' && supportRequestId.length > 0) {
    return supportRequestId
  }

  if (typeof supportRequestId === 'number') {
    return String(supportRequestId)
  }

  return null
}

function isSupportNotification(notification: AppNotification): boolean {
  return (
    notification.type === 'support_created' ||
    notification.type === 'support_reply' ||
    getLinkedSupportRequestId(notification) !== null
  )
}

export function NotificationsPage() {
  const { can } = useAuth()
  const { refreshUnreadSummary, revision, markNotificationRead } = useNotifications()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [markingId, setMarkingId] = useState<string | null>(null)
  const [markingAll, setMarkingAll] = useState(false)

  const loadNotifications = useCallback(async () => {
    try {
      const response = await notificationService.list({ page, per_page: perPage })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setNotifications(items)
      setMeta(nextMeta)
    } catch {
      setNotifications([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [page, perPage])

  useEffect(() => {
    void loadNotifications()
    void refreshUnreadSummary()
  }, [loadNotifications, refreshUnreadSummary, revision])

  const handleMarkRead = async (id: string) => {
    setMarkingId(id)

    try {
      await markNotificationRead(id)
      setNotifications((current) =>
        current.map((item) =>
          item.id === id ? { ...item, read_at: item.read_at ?? new Date().toISOString() } : item,
        ),
      )
    } catch {
      // handled globally
    } finally {
      setMarkingId(null)
    }
  }

  const handleViewSupportRequest = (notification: AppNotification) => {
    if (notification.read_at) {
      return
    }

    void markNotificationRead(notification.id).then(() => {
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id
            ? { ...item, read_at: item.read_at ?? new Date().toISOString() }
            : item,
        ),
      )
    })
  }

  const handleMarkAllRead = async () => {
    setMarkingAll(true)

    try {
      await notificationService.markAllRead()
      await Promise.all([loadNotifications(), refreshUnreadSummary()])
    } catch {
      // handled globally
    } finally {
      setMarkingAll(false)
    }
  }

  const supportTicketPath = (ticketId: string) => {
    if (can('support-requests-list-all')) {
      return `/admin/support?ticket=${ticketId}`
    }

    return `/support?ticket=${ticketId}`
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Notifications</h1>
            <p className="mt-1 text-sm text-ink-600">
              Updates about your cases, reviews, and support tickets.
            </p>
          </div>

          <Button
            variant="secondary"
            disabled={markingAll}
            onClick={() => void handleMarkAllRead()}
          >
            {markingAll ? 'Marking…' : 'Mark All Read'}
          </Button>
        </div>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          {hasLoaded && notifications.length === 0 ? (
            <p className="px-4 py-6 text-sm text-ink-500">No notifications.</p>
          ) : (
            <ul>
              {notifications.map((notification) => {
                const linkedCaseId = getLinkedCaseId(notification)
                const linkedSupportId = getLinkedSupportRequestId(notification)
                const isUnread = !notification.read_at
                const canOpenSupport =
                  linkedSupportId !== null &&
                  isSupportNotification(notification) &&
                  (can('support-requests-list-all') || can('support-requests-list'))

                return (
                  <li
                    key={notification.id}
                    className={cn(
                      'border-b border-parch-50 px-4 py-4 last:border-0',
                      isUnread && 'bg-green-50/40',
                    )}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-ink-900">
                            {notification.title}
                          </p>
                          {isUnread && (
                            <span className="rounded-full bg-green-600 px-2 py-0.5 text-xs font-medium text-white">
                              New
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-ink-700">{notification.body}</p>
                        <p className="mt-2 text-xs text-ink-500">
                          {notification.created_at
                            ? new Date(notification.created_at).toLocaleString()
                            : '—'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-3">
                          {linkedCaseId && can('cases-show') && (
                            <Link
                              to={`/cases/${linkedCaseId}`}
                              className="inline-block text-sm font-medium text-green-700 hover:text-green-800"
                            >
                              View case
                            </Link>
                          )}
                          {canOpenSupport && linkedSupportId && (
                            <Link
                              to={supportTicketPath(linkedSupportId)}
                              className="inline-block text-sm font-medium text-green-700 hover:text-green-800"
                              onClick={() => handleViewSupportRequest(notification)}
                            >
                              View support request
                            </Link>
                          )}
                        </div>
                      </div>

                      {isUnread && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={markingId === notification.id}
                          onClick={() => void handleMarkRead(notification.id)}
                        >
                          {markingId === notification.id ? 'Marking…' : 'Mark Read'}
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(next) => {
            setPerPage(next)
            setPage(1)
          }}
        />
      </div>
    </AppShell>
  )
}
